<template>
  <div class="create-invoice-view">
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="handleCancel">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="header-info">
          <h1 class="page-heading">Nouvelle facture fournisseur</h1>
          <p class="page-subtitle">{{ supplier?.name || 'Chargement...' }}</p>
        </div>
      </div>
      <div class="actions">
        <button class="secondary-btn" @click="handleCancel">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          Annuler
        </button>
        <button class="primary-btn" :disabled="isSubmitting || !canSubmit" @click="handleSubmit">
          <span v-if="isSubmitting" class="spinner-small"></span>
          <template v-else>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Générer la facture
          </template>
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement des commandes...</p>
    </div>

    <div v-else class="layout">
      <div class="main-column">
        <div class="card">
          <div class="card-header">
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div class="card-title-group">
              <h2 class="card-title">Informations de la facture</h2>
              <p class="card-subtitle">Définissez les dates et notes</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label>
                Date de facture
                <span class="required">*</span>
              </label>
              <div class="input-with-icon">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <input type="date" v-model="form.invoice_date" />
              </div>
              <span v-if="errors.invoice_date" class="field-error">
                {{ errors.invoice_date }}
              </span>
            </div>

            <div class="form-group">
              <label>Date d'échéance</label>
              <div class="input-with-icon">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <input type="date" v-model="form.due_date" :min="form.invoice_date" />
              </div>
            </div>

            <div class="form-group full-width">
              <label>Notes</label>
              <div class="textarea-wrapper">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <textarea v-model="form.notes" rows="3" placeholder="Notes additionnelles pour cette facture..."></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <div class="card-title-group">
              <h2 class="card-title">Commandes à facturer</h2>
              <p class="card-subtitle">Sélectionnez les commandes à inclure dans la facture</p>
            </div>
          </div>

          <div v-if="availableOrders.length === 0" class="empty-state">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3>Aucune commande disponible</h3>
            <p>Ce fournisseur n'a pas de commandes actives à facturer</p>
          </div>

          <div v-else class="orders-list">
            <div v-for="order in availableOrders" :key="order.id" class="order-card"
              :class="{ selected: isOrderSelected(order.id) }">
              <div class="order-select">
                <input type="checkbox" :id="`order-${order.id}`" :value="order.id" v-model="selectedOrderIds"
                  @change="toggleOrder(order)" />
                <label :for="`order-${order.id}`" class="checkbox-label"></label>
              </div>

              <div class="order-content" @click="toggleOrder(order)">
                <div class="order-header">
                  <div class="order-reference">
                    <div class="reference-badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div class="reference-info">
                      <div class="reference-text">{{ order.reference }}</div>
                      <div class="order-date">{{ formatDate(order.order_date) }}</div>
                    </div>
                  </div>
                  <div class="order-amount">
                    <div class="amount-label">Montant</div>
                    <div class="amount-value">{{ formatCurrency(calculateOrderTotal(order)) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sidebar">
        <div class="card summary-card">
          <div class="summary-header">
            <div class="summary-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <h3>Résumé de la facture</h3>
          </div>

          <div class="summary-content">
            <div class="summary-item">
              <div class="summary-label">Fournisseur</div>
              <div class="summary-value-text">{{ supplier?.name || '-' }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item">
              <div class="summary-label">Commandes sélectionnées</div>
              <div class="summary-value">{{ selectedOrderIds.length }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item">
              <div class="summary-label">Sous-total</div>
              <div class="summary-value">{{ formatCurrency(totalAmount) }}</div>
            </div>

            <div v-if="totalDiscount > 0" class="summary-item">
              <div class="summary-label">Remise totale</div>
              <div class="summary-value discount-value">-{{ formatCurrency(totalDiscount) }}</div>
            </div>

            <div v-if="totalTax > 0" class="summary-item">
              <div class="summary-label">Taxe totale</div>
              <div class="summary-value">{{ formatCurrency(totalTax) }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item highlight">
              <div class="summary-label">Montant total</div>
              <div class="summary-value primary">{{ formatCurrency(grandTotal) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import purchaseOrderService from '@/services/purchaseOrderService'
import supplierService from '@/services/supplierService'
import supplierInvoiceService from '@/services/supplierInvoiceService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'CreateSupplierInvoiceView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toastStore = useToastStore()

    const loading = ref(true)
    const isSubmitting = ref(false)
    const supplier = ref(null)
    const availableOrders = ref([])
    const selectedOrderIds = ref([])
    const ordersData = ref({})

    const form = ref({
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: ''
    })

    const errors = ref({})

    const canSubmit = computed(() => {
      return form.value.invoice_date && selectedOrderIds.value.length > 0
    })

    const totalAmount = computed(() => {
      return selectedOrderIds.value.reduce((sum, orderId) => {
        return sum + (Number(ordersData.value[orderId]?.amount) || 0)
      }, 0)
    })

    const totalDiscount = computed(() => {
      return selectedOrderIds.value.reduce((sum, orderId) => {
        return sum + (Number(ordersData.value[orderId]?.discount) || 0)
      }, 0)
    })

    const totalTax = computed(() => {
      return selectedOrderIds.value.reduce((sum, orderId) => {
        return sum + (Number(ordersData.value[orderId]?.tax_amount) || 0)
      }, 0)
    })

    const grandTotal = computed(() => {
      return totalAmount.value - totalDiscount.value + totalTax.value
    })

    const loadSupplierAndOrders = async () => {
      const supplierId = route.params.supplierId
      if (!supplierId) {
        toastStore.addToast({
          type: 'error',
          message: 'ID fournisseur manquant',
          duration: 3000
        })
        router.push({ name: 'suppliers' })
        return
      }

      try {
        loading.value = true

        // Load supplier details
        const supplierRes = await supplierService.getSupplier(supplierId)
        supplier.value = supplierRes.data?.data || supplierRes.data || {}

        // Load active purchase orders for this supplier
        const ordersRes = await purchaseOrderService.getPurchaseOrders({
          supplier_id: supplierId,
          status: 'active',
          with_out_invoice: false
        })

        availableOrders.value = Array.isArray(ordersRes.data)
          ? ordersRes.data
          : (ordersRes.data.data || ordersRes.data.items || [])

        // Initialize orders data
        availableOrders.value.forEach(order => {
          ordersData.value[order.id] = {
            purchase_order_id: order.id,
            items_count: order.items_count,
            amount: order.total_amount,
            status: 'included',
            notes: ''
          }
        })
      } catch (e) {
        console.error('Error loading data:', e)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement des données',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const calculateOrderTotal = (order) => {
      return order.total_amount;
    }

    const calculateOrderNetTotal = (orderId) => {
      const data = ordersData.value[orderId]
      if (!data) return 0
      return (Number(data.amount) || 0) - (Number(data.discount) || 0) + (Number(data.tax_amount) || 0)
    }

    const isOrderSelected = (orderId) => {
      return selectedOrderIds.value.includes(orderId)
    }

    const toggleOrder = (order) => {
      const index = selectedOrderIds.value.indexOf(order.id)
      if (index > -1) {
        selectedOrderIds.value.splice(index, 1)
      } else {
        selectedOrderIds.value.push(order.id)
      }
    }

    const getOrderData = (orderId) => {
      return ordersData.value[orderId] || {}
    }

    const handleSubmit = async () => {
      errors.value = {}

      if (!form.value.invoice_date) {
        errors.value.invoice_date = 'La date de facture est requise'
        return
      }

      if (selectedOrderIds.value.length === 0) {
        toastStore.addToast({
          type: 'error',
          message: 'Veuillez sélectionner au moins une commande',
          duration: 3000
        })
        return
      }

      try {
        isSubmitting.value = true

        const payload = {
          supplier_id: supplier.value.id,
          invoice_date: form.value.invoice_date,
          due_date: form.value.due_date || null,
          notes: form.value.notes || '',
          purchase_orders: selectedOrderIds.value.map(orderId => ({
            purchase_order_id: ordersData.value[orderId].purchase_order_id,
            amount: Number(ordersData.value[orderId].amount) || 0,
            discount: Number(ordersData.value[orderId].discount) || 0,
            tax_amount: Number(ordersData.value[orderId].tax_amount) || 0,
            status: ordersData.value[orderId].status || 'included',
            notes: ordersData.value[orderId].notes || ''
          }))
        }

        const res = await supplierInvoiceService.createSupplierInvoice(payload)
        const invoiceId = res.data?.data?.id || res.data?.id

        toastStore.addToast({
          type: 'success',
          message: 'Facture créée avec succès',
          duration: 3000
        })

        router.push({ name: 'supplier-invoice-details', params: { id: invoiceId } })
      } catch (e) {
        console.error('Error creating invoice:', e)
        const errorMessage = e.response?.data?.message || 'Erreur lors de la création de la facture'
        toastStore.addToast({
          type: 'error',
          message: errorMessage,
          duration: 3000
        })

        if (e.response?.data?.errors) {
          errors.value = e.response.data.errors
        }
      } finally {
        isSubmitting.value = false
      }
    }

    const handleCancel = () => {
      if (selectedOrderIds.value.length > 0 || form.value.notes) {
        if (confirm('Êtes-vous sûr de vouloir annuler ? Les modifications seront perdues.')) {
          router.push({ name: 'supplier-detail', params: { id: supplier.value.id } })
        }
      } else {
        router.push({ name: 'supplier-detail', params: { id: supplier.value.id } })
      }
    }

    const formatCurrency = (amount) => {
      return supplierInvoiceService.formatCurrency(amount)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return supplierInvoiceService.formatDate(dateString)
    }

    onMounted(loadSupplierAndOrders)

    return {
      loading,
      isSubmitting,
      supplier,
      availableOrders,
      selectedOrderIds,
      ordersData,
      form,
      errors,
      canSubmit,
      totalAmount,
      totalDiscount,
      totalTax,
      grandTotal,
      calculateOrderTotal,
      calculateOrderNetTotal,
      isOrderSelected,
      toggleOrder,
      getOrderData,
      handleSubmit,
      handleCancel,
      formatCurrency,
      formatDate
    }
  }
})
</script>

<style scoped>
.create-invoice-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #0A5C6B;
}

.back-btn svg {
  width: 20px;
  height: 20px;
  stroke: #475569;
  stroke-width: 2;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.primary-btn,
.secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.primary-btn {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.2);
}

.primary-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary-btn {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.secondary-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.primary-btn svg,
.secondary-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.layout {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 24px;
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.card:last-child {
  margin-bottom: 0;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 2px solid #f1f5f9;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon svg {
  width: 20px;
  height: 20px;
  stroke: #0284c7;
  stroke-width: 2;
}

.card-title-group {
  flex: 1;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.card-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.form-grid {
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.required {
  color: #c62828;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  transition: all 0.2s;
  outline: none;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  stroke: #94a3b8;
  stroke-width: 2;
  pointer-events: none;
}

.input-with-icon input {
  padding-left: 44px;
}

.textarea-wrapper {
  position: relative;
}

.textarea-wrapper .input-icon {
  top: 18px;
  transform: none;
}

.textarea-wrapper textarea {
  padding-left: 44px;
  resize: vertical;
}

.field-error {
  color: #c62828;
  font-size: 12px;
  margin-top: 6px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.orders-list {
  padding: 20px 24px;
}

.order-card {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 16px;
  transition: all 0.2s;
  cursor: pointer;
}

.order-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.order-card.selected {
  border-color: #0A5C6B;
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.15);
}

.order-card:last-child {
  margin-bottom: 0;
}

.order-select {
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
}

.order-select input[type="checkbox"] {
  display: none;
}

.checkbox-label {
  width: 24px;
  height: 24px;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  position: relative;
}

.order-select input[type="checkbox"]:checked + .checkbox-label {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  border-color: #0A5C6B;
}

.order-select input[type="checkbox"]:checked + .checkbox-label::after {
  content: '';
  position: absolute;
  width: 8px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  top: 3px;
}

.order-content {
  flex: 1;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.order-reference {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reference-badge {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.reference-badge svg {
  width: 20px;
  height: 20px;
  stroke: #0284c7;
  stroke-width: 2;
}

.reference-text {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.order-date {
  font-size: 13px;
  color: #64748b;
}

.order-amount {
  text-align: right;
}

.amount-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.amount-value {
  font-size: 18px;
  font-weight: 800;
  color: #0A5C6B;
}

.order-details {
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.detail-group {
  display: flex;
  flex-direction: column;
}

.detail-group.full-width {
  grid-column: 1 / -1;
}

.detail-group label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
}

.detail-group input,
.detail-group select {
  padding: 8px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  transition: all 0.2s;
}

.detail-group input:focus,
.detail-group select:focus {
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.order-summary {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
  color: #475569;
}

.summary-row.discount {
  color: #c62828;
}

.summary-row.total {
  border-top: 1px solid #e2e8f0;
  margin-top: 6px;
  padding-top: 12px;
  font-weight: 700;
  font-size: 15px;
  color: #0f172a;
}

.summary-value {
  font-weight: 600;
}

.summary-row.total .summary-value {
  font-size: 16px;
  color: #0A5C6B;
}

.summary-card {
  position: sticky;
  top: 24px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-bottom: 2px solid #f1f5f9;
}

.summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-icon svg {
  width: 20px;
  height: 20px;
  stroke: #2e7d32;
  stroke-width: 2;
}

.summary-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.summary-content {
  padding: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item.highlight {
  padding: 16px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  margin-left: -4px;
  margin-right: -4px;
}

.summary-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.summary-value {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.summary-value.primary {
  color: #0A5C6B;
}

.summary-value.discount-value {
  color: #c62828;
}

.summary-value-text {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  text-align: right;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 16px 0;
}

@media (max-width: 1024px) {
  .layout {
    grid-template-columns: 1fr;
  }

  .summary-card {
    position: static;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .create-invoice-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
  }

  .actions {
    width: 100%;
  }

  .primary-btn,
  .secondary-btn {
    flex: 1;
    justify-content: center;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .order-header {
    flex-direction: column;
    gap: 12px;
  }

  .order-amount {
    text-align: left;
  }
}
</style>
